// src/pages/Partners.js
import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import api from '../lib/api'

// ── Constants ─────────────────────────────────────────────────────────────

const TAG_COLORS = ['badge-blue','badge-green','badge-purple','badge-yellow','badge-red','badge-gray']
function tagColor(t) { return TAG_COLORS[Math.abs([...t].reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length] }

const CONTACT_TYPES = ['Investor', 'GP', 'Partner Advisor', 'Service Provider', 'RGA Team']

const SORTABLE_COLS = {
  name:      { label: 'Name',      fn: (a, b) => (a.name || '').localeCompare(b.name || '') },
  firm:      { label: 'Firm',      fn: (a, b) => (a.primary_firm?.name || '').localeCompare(b.primary_firm?.name || '') },
  agreement: { label: 'Partnership Agreement', fn: (a, b) => (a.partnership_agreement ? 1 : 0) - (b.partnership_agreement ? 1 : 0) },
  licensed:  { label: 'Licensed',  fn: (a, b) => (a.is_licensed ? 1 : 0) - (b.is_licensed ? 1 : 0) },
  mandates:  { label: 'Active Mandates', fn: (a, b) => (a.active_mandates?.length ?? 0) - (b.active_mandates?.length ?? 0) },
  notes:     { label: 'Notes',     fn: (a, b) => (a.notes || '').localeCompare(b.notes || '') },
}

// ── Helpers ───────────────────────────────────────────────────────────────

function todayStr() { return new Date().toISOString().slice(0, 10) }

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Shared UI ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 560, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children || '—'}</div>
    </div>
  )
}

function SortHeader({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}>
      {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.25 }}>▲</span>}
    </th>
  )
}

// ── Multi-Select Filter Dropdown ─────────────────────────────────────────

function MultiSelectFilter({ label, options, selected, onToggle }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label} {selected.length > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#fff', borderRadius: 8,
            padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
          }}>{selected.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 10,
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, marginTop: 4, minWidth: 180, maxHeight: 260, overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
          onMouseLeave={() => setOpen(false)}>
          {options.map(o => {
            const checked = selected.includes(o)
            return (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                cursor: 'pointer', fontSize: 12,
                background: checked ? 'var(--accent-muted)' : 'transparent',
              }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  style={{ accentColor: 'var(--accent)' }} />
                <span className={`badge ${tagColor(o)}`} style={{ fontSize: 10 }}>{o}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Tag Input Component ──────────────────────────────────────────────────

function TagInput({ tags, onChange, suggestions = [] }) {
  const [input, setInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)

  const filteredSuggestions = useMemo(() => {
    if (!input.trim()) return []
    const q = input.toLowerCase()
    return suggestions
      .filter(s => s.toLowerCase().includes(q) && !tags.includes(s))
      .slice(0, 8)
  }, [input, suggestions, tags])

  function addTag(t) {
    const tag = t.trim()
    if (tag && !tags.includes(tag)) onChange([...tags, tag])
    setInput('')
    setShowSuggestions(false)
  }

  function removeTag(t) { onChange(tags.filter(x => x !== t)) }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      addTag(input)
    }
    if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags[tags.length - 1])
    }
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          value={input}
          onChange={e => { setInput(e.target.value); setShowSuggestions(true) }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tags.length ? 'Add another tag…' : 'Type a tag and press Enter'}
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 6, marginTop: 2, maxHeight: 180, overflow: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            {filteredSuggestions.map(s => (
              <div key={s}
                onMouseDown={() => addTag(s)}
                style={{
                  padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                  color: 'var(--text-primary)',
                }}
                onMouseEnter={e => e.target.style.background = 'var(--accent-muted)'}
                onMouseLeave={e => e.target.style.background = 'transparent'}>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
          {tags.map(t => (
            <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t}
              <span onClick={() => removeTag(t)}
                style={{ cursor: 'pointer', opacity: 0.7, fontWeight: 700, fontSize: 13, lineHeight: 1 }}>
                ×
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Export helpers ────────────────────────────────────────────────────────

function exportPartners(rows, format) {
  const data = rows.map(p => ({
    'Name':                  p.name ?? '',
    'Firm':                  p.primary_firm?.name ?? '',
    'Partnership Agreement': p.partnership_agreement ? 'Yes' : 'No',
    'Licensed':              p.is_licensed ? 'Yes' : 'No',
    'Active Mandates':       (p.active_mandates ?? []).map(m => m.mandate_name).join(', '),
    'Tags':                  (p.tags ?? []).join(', '),
    'Notes':                 p.notes ?? '',
  }))

  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Partners')

  const filename = `RGA_Partners_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

// ── New Partner form ─────────────────────────────────────────────────────

function PartnerForm({ firms, allContacts, users, tagSuggestions, onSubmit, onCancel, submitting, error }) {
  const [form, setForm] = useState({
    name:                '',
    primary_firm_id:     '',
    title_role:          '',
    contact_type:        ['Partner Advisor'],
    email:               '',
    phone:               '',
    introduced_by:       '',
    relationship_owner:  '',
    tags:                [],
    partnership_agreement: false,
    is_licensed:         false,
  })
  const [err, setErr] = useState(null)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleType(t) {
    setForm(p => ({
      ...p,
      contact_type: p.contact_type.includes(t)
        ? p.contact_type.filter(x => x !== t)
        : [...p.contact_type, t],
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return setErr('Name is required.')
    const payload = { name: form.name.trim() }
    if (form.primary_firm_id)     payload.primary_firm_id     = form.primary_firm_id
    if (form.title_role)          payload.title_role           = form.title_role.trim()
    if (form.contact_type.length) payload.contact_type         = form.contact_type
    if (form.email.trim())        payload.email                = form.email.split(',').map(s => s.trim()).filter(Boolean)
    if (form.phone.trim())        payload.phone                = form.phone.split(',').map(s => s.trim()).filter(Boolean)
    if (form.introduced_by)       payload.introduced_by        = form.introduced_by
    if (form.relationship_owner)  payload.relationship_owner   = form.relationship_owner
    if (form.tags.length)         payload.tags                 = form.tags
    payload.partnership_agreement = form.partnership_agreement
    payload.is_licensed           = form.is_licensed
    try { await onSubmit(payload) }
    catch (err2) { setErr(err2.response?.data?.error || err2.message) }
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ErrorBanner msg={err || error} />

      {/* Name */}
      <div className="input-group">
        <label className="input-label">Name *</label>
        <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="Full name" autoFocus />
      </div>

      {/* Primary Firm + Title */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Primary Firm</label>
          <select className="select" value={form.primary_firm_id} onChange={e => set('primary_firm_id', e.target.value)}>
            <option value="">None</option>
            {(firms ?? []).map(f => <option key={f.firm_id} value={f.firm_id}>{f.name}</option>)}
          </select>
        </div>
        <div className="input-group">
          <label className="input-label">Title / Role</label>
          <input className="input" value={form.title_role} onChange={e => set('title_role', e.target.value)}
            placeholder="e.g. Partner Advisor" />
        </div>
      </div>

      {/* Contact Type */}
      <div className="input-group">
        <label className="input-label">Contact Type</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {CONTACT_TYPES.map(t => {
            const checked = form.contact_type.includes(t)
            return (
              <label key={t} style={{
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                fontSize: 12, padding: '5px 10px', borderRadius: 5,
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                background: checked ? 'var(--accent-muted)' : 'transparent',
                color: checked ? 'var(--text-primary)' : 'var(--text-secondary)',
                transition: 'all 0.12s',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggleType(t)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            )
          })}
        </div>
      </div>

      {/* Email + Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Email(s)</label>
          <input className="input" value={form.email} onChange={e => set('email', e.target.value)}
            placeholder="a@b.com, c@d.com" />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Comma-separated</div>
        </div>
        <div className="input-group">
          <label className="input-label">Phone(s)</label>
          <input className="input" value={form.phone} onChange={e => set('phone', e.target.value)}
            placeholder="+1 555 0100" />
        </div>
      </div>

      {/* Introduced By + Relationship Owner */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Introduced By</label>
          <select className="select" value={form.introduced_by} onChange={e => set('introduced_by', e.target.value)}>
            <option value="">None</option>
            {(allContacts ?? []).map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
          </select>
        </div>
        <div className="input-group">
          <label className="input-label">Relationship Owner</label>
          <select className="select" value={form.relationship_owner} onChange={e => set('relationship_owner', e.target.value)}>
            <option value="">None</option>
            {(users ?? []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      {/* Partnership Agreement + Licensed toggles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.partnership_agreement}
              onChange={e => set('partnership_agreement', e.target.checked)}
              style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: 16, height: 16 }} />
            Partnership Agreement
          </label>
        </div>
        <div className="input-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.is_licensed}
              onChange={e => set('is_licensed', e.target.checked)}
              style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: 16, height: 16 }} />
            Licensed
          </label>
        </div>
      </div>

      {/* Tags */}
      <div className="input-group">
        <label className="input-label">Tags</label>
        <TagInput tags={form.tags} onChange={v => set('tags', v)} suggestions={tagSuggestions ?? []} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
        borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// ── Partner detail modal ─────────────────────────────────────────────────

function PartnerDetailModal({ partner, onClose, firms, allContacts, users, tagSuggestions, onToggleField }) {
  const [editMode, setEditMode] = useState(false)
  const qc = useQueryClient()

  // Activity timeline for this partner
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['interactions', { contact_id: partner.contact_id }],
    queryFn: () => api.get('/api/interactions', { params: { contact_id: partner.contact_id } }).then(r => r.data.data ?? []),
  })
  const activity = useMemo(() => activityData ?? [], [activityData])

  const INT_TYPE_BADGE = {
    'Email': 'badge-blue', 'Meeting': 'badge-purple',
    'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
  }

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/contacts/${partner.contact_id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contactTags'] })
      setEditMode(false)
    },
  })

  const resolvedOwnerName = partner.owner?.name
    || (users ?? []).find(u => u.id === partner.relationship_owner)?.name

  if (editMode) {
    return (
      <Modal title={`Edit - ${partner.name}`} onClose={onClose} width={600}>
        <PartnerForm
          firms={firms}
          allContacts={(allContacts ?? []).filter(c => c.contact_id !== partner.contact_id)}
          users={users}
          tagSuggestions={tagSuggestions}
          onSubmit={updateMutation.mutateAsync}
          onCancel={() => setEditMode(false)}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.response?.data?.error}
        />
      </Modal>
    )
  }

  return (
    <Modal title={partner.name} onClose={onClose} width={600}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{partner.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
              {partner.title_role ?? ''}
              {partner.primary_firm && <span> &middot; {partner.primary_firm.name}</span>}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>Edit</button>
        </div>

        {/* Status badges */}
        <div style={{ display: 'flex', gap: 10 }}>
          <span className={`badge ${partner.partnership_agreement ? 'badge-green' : 'badge-gray'}`}>
            {partner.partnership_agreement ? '✓ Partnership Agreement' : '✗ No Agreement'}
          </span>
          <span className={`badge ${partner.is_licensed ? 'badge-green' : 'badge-gray'}`}>
            {partner.is_licensed ? '✓ Licensed' : '✗ Not Licensed'}
          </span>
        </div>

        <div className="divider" />

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Email">
            {(partner.email ?? []).map((e, i) => (
              <div key={i}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>
            ))}
          </Field>
          <Field label="Phone">
            {(partner.phone ?? []).join(', ') || null}
          </Field>
          <Field label="Relationship Owner">{resolvedOwnerName || '—'}</Field>
          <Field label="Last Interaction">{formatDate(partner.last_interaction)}</Field>
        </div>

        {/* Notes */}
        {partner.notes && (
          <>
            <div className="divider" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Notes
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                {partner.notes}
              </div>
            </div>
          </>
        )}

        {/* Active Mandates */}
        {(partner.active_mandates ?? []).length > 0 && (
          <>
            <div className="divider" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Active Mandates
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {partner.active_mandates.map(m => (
                  <span key={m.mandate_id} className="badge badge-blue">{m.mandate_name}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Tags */}
        {(partner.tags ?? []).length > 0 && (
          <>
            <div className="divider" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Tags
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {partner.tags.map(t => (
                  <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 11 }}>{t}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Activity timeline */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Activity Timeline
          </div>
          {activityLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <div className="spinner" />
            </div>
          ) : activity.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
              No interactions recorded yet.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {activity.map((i, idx) => (
                <div key={i.interaction_id} style={{
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                  borderBottom: idx < activity.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span className={`badge ${INT_TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0 }}>
                    {i.type}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.subject_title || '(no subject)'}
                    </div>
                    {i.mandate && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                        {i.mandate.mandate_name}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                    {formatDate(i.date)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Partners() {
  const [search, setSearch]               = useState('')
  const [agreementFilter, setAgreement]   = useState('')
  const [licensedFilter, setLicensed]     = useState('')
  const [tagFilter, setTagFilter]         = useState([])
  const [mandateFilter, setMandateFilter] = useState([])
  const [sortCol, setSortCol]             = useState('name')
  const [sortDir, setSortDir]             = useState('asc')
  const [selected, setSelected]           = useState(null)
  const [createOpen, setCreate]           = useState(false)
  const [toDelete, setToDelete]           = useState(null)
  const qc = useQueryClient()

  const { data: partnersData, isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get('/api/partners').then(r => r.data.data ?? []),
  })
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: tagSuggestions } = useQuery({
    queryKey: ['contactTags'],
    queryFn: () => api.get('/api/contacts/tags').then(r => r.data.data ?? []),
    staleTime: 60 * 1000,
  })
  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
    staleTime: 60 * 1000,
  })

  const partners  = useMemo(() => partnersData ?? [], [partnersData])
  const users     = useMemo(() => usersData ?? [], [usersData])
  const contacts  = useMemo(() => contactsData ?? [], [contactsData])
  const firms     = firmsData ?? []
  const allMandates = useMemo(() => mandatesData ?? [], [mandatesData])

  // All mandate names from the mandates API for the filter dropdown
  const allMandateNames = useMemo(() => {
    return allMandates.map(m => m.mandate_name).filter(Boolean).sort()
  }, [allMandates])

  // All unique tags — combine partner tags with the global tag suggestions
  const allTags = useMemo(() => {
    const s = new Set(tagSuggestions ?? [])
    for (const p of partners) { for (const t of (p.tags ?? [])) s.add(t) }
    return Array.from(s).sort()
  }, [partners, tagSuggestions])

  function toggleTagFilter(t) { setTagFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }
  function toggleMandateFilter(m) { setMandateFilter(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]) }

  // Boolean() — without it, `... || 0 || 0` returns the number 0 which React renders as literal "0"
  const hasFilters = Boolean(search || agreementFilter || licensedFilter || tagFilter.length || mandateFilter.length)
  const activeFilterCount = (agreementFilter ? 1 : 0) + (licensedFilter ? 1 : 0) + (tagFilter.length ? 1 : 0) + (mandateFilter.length ? 1 : 0)

  function clearAllFilters() {
    setSearch(''); setAgreement(''); setLicensed(''); setTagFilter([]); setMandateFilter([])
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const filtered = useMemo(() => {
    let list = partners.filter(p => {
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false
      if (agreementFilter === 'yes' && !p.partnership_agreement) return false
      if (agreementFilter === 'no' && p.partnership_agreement) return false
      if (licensedFilter === 'yes' && !p.is_licensed) return false
      if (licensedFilter === 'no' && p.is_licensed) return false
      if (tagFilter.length && !tagFilter.some(t => (p.tags ?? []).includes(t))) return false
      if (mandateFilter.length && !mandateFilter.some(m => (p.active_mandates ?? []).some(am => am.mandate_name === m))) return false
      return true
    })

    const sortFn = SORTABLE_COLS[sortCol]?.fn
    if (sortFn) {
      list = [...list].sort((a, b) => sortDir === 'asc' ? sortFn(a, b) : sortFn(b, a))
    }
    return list
  }, [partners, search, agreementFilter, licensedFilter, tagFilter, mandateFilter, sortCol, sortDir])

  // Toggle mutation for partnership_agreement / is_licensed
  const toggleMutation = useMutation({
    mutationFn: ({ id, field, value }) => api.patch(`/api/contacts/${id}`, { [field]: value }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const handleToggle = useCallback((partner, field) => {
    const newValue = !partner[field]
    toggleMutation.mutate({ id: partner.contact_id, field, value: newValue })
  }, [toggleMutation])

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/contacts', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contactTags'] })
      setCreate(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/contacts/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['partners'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      setToDelete(null)
      setSelected(prev => prev?.contact_id === id ? null : prev)
    },
  })

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Partners</h1>
          <p>{isLoading ? 'Loading...' : `${partners.length} partner advisor${partners.length !== 1 ? 's' : ''}`}
            {' '}<span style={{ color: 'var(--text-muted)' }}>— Partner advisors and their engagement status</span></p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => exportPartners(filtered, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportPartners(filtered, 'xlsx')}>Export Excel</button>
          <button className="btn btn-primary" onClick={() => setCreate(true)}>+ New Partner</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by name..."
          value={search} onChange={e => setSearch(e.target.value)} />

        <select className="select" style={{ maxWidth: 200, fontSize: 12 }} value={agreementFilter}
          onChange={e => setAgreement(e.target.value)}>
          <option value="">Partnership Agreement</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>

        <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={licensedFilter}
          onChange={e => setLicensed(e.target.value)}>
          <option value="">Licensed</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>

        <MultiSelectFilter label="Mandates" options={allMandateNames}
          selected={mandateFilter} onToggle={toggleMandateFilter} />

        <MultiSelectFilter label="Tags" options={allTags}
          selected={tagFilter} onToggle={toggleTagFilter} />

        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Clear all{activeFilterCount > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#fff', borderRadius: 8,
                padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
              }}>{activeFilterCount}</span>
            )}
          </button>
        )}

        {filtered.length !== partners.length && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {filtered.length} of {partners.length}
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortHeader col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Name</SortHeader>
                <SortHeader col="firm" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Firm</SortHeader>
                <SortHeader col="agreement" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Partnership Agreement</SortHeader>
                <SortHeader col="licensed" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Licensed</SortHeader>
                <SortHeader col="mandates" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Active Mandates</SortHeader>
                <th>Tags</th>
                <SortHeader col="notes" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Notes</SortHeader>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8}>
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <h3>{hasFilters ? 'No partners match your filters' : 'No partners yet'}</h3>
                    <p>{!hasFilters && 'Create your first partner advisor to get started.'}</p>
                  </div>
                </td></tr>
              ) : (
                filtered.map(p => (
                  <tr key={p.contact_id}>
                    <td style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setSelected(p)}>
                      {p.name}
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                      {p.primary_firm?.name ?? '—'}
                    </td>
                    <td>
                      <span
                        className={`badge ${p.partnership_agreement ? 'badge-green' : 'badge-gray'}`}
                        style={{ cursor: 'pointer', fontSize: 11 }}
                        onClick={() => handleToggle(p, 'partnership_agreement')}
                        title="Click to toggle"
                      >
                        {p.partnership_agreement ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${p.is_licensed ? 'badge-green' : 'badge-gray'}`}
                        style={{ cursor: 'pointer', fontSize: 11 }}
                        onClick={() => handleToggle(p, 'is_licensed')}
                        title="Click to toggle"
                      >
                        {p.is_licensed ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(p.active_mandates ?? []).length === 0
                          ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                          : (p.active_mandates ?? []).map(m => (
                              <span key={m.mandate_id} className="badge badge-blue" style={{ fontSize: 10 }}>
                                {m.mandate_name}
                              </span>
                            ))
                        }
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(p.tags ?? []).slice(0, 3).map(t => (
                          <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>
                        ))}
                        {(p.tags ?? []).length > 3 && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{p.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180 }}>
                      {p.notes
                        ? <span title={p.notes}>{p.notes.length > 50 ? p.notes.slice(0, 50) + '...' : p.notes}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>
                      }
                    </td>
                    <td style={{ width: 36, paddingRight: 8 }}>
                      <button
                        onClick={() => setToDelete(p)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                          display: 'inline-flex', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        title="Delete partner"
                      ><TrashIcon /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {createOpen && (
        <Modal title="New Partner" onClose={() => setCreate(false)}>
          <PartnerForm
            firms={firms}
            allContacts={contacts}
            users={users}
            tagSuggestions={tagSuggestions ?? []}
            onSubmit={createMutation.mutateAsync}
            onCancel={() => setCreate(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </Modal>
      )}

      {/* Detail modal */}
      {selected && (
        <PartnerDetailModal
          partner={selected}
          onClose={() => setSelected(null)}
          firms={firms}
          allContacts={contacts}
          users={users}
          tagSuggestions={tagSuggestions ?? []}
          onToggleField={handleToggle}
        />
      )}

      {/* Delete confirmation */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.name}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.contact_id)}
          deleting={deleteMutation.isPending}
        />
      )}
    </div>
  )
}
